import {
  REGIONS, computeAtlas, eloUpdate, regionOfRealm,
  type AnswerEvent, type Atlas, type RegionEstimate,
} from './cognitiveEngine';

/**
 * The Atlas Rank ladder. What the games that actually work give their players:
 * a name to say, a number to grow, a delta per match, a next rung. Everything
 * here reads the Elo the engine already computes; nothing new is stored.
 *
 * Ladder names deliberately avoid every archetype name (THE CARTOGRAPHER and
 * THE DRIFTER are taken): the archetype says how you play, the rank says how
 * far you have climbed.
 *
 * Light climbs the ladder: the bottom rung is dark gunmetal, the top two rungs
 * literally glow in the UI.
 */
export interface RankTier {
  name: string;
  min: number;
  color: string;
  glow?: boolean;
}

export const RANK_TIERS: RankTier[] = [
  { name: 'SCOUT',     min: -Infinity, color: '#707680' },
  { name: 'VOYAGER',   min: 1000,      color: '#b0764a' },
  { name: 'NAVIGATOR', min: 1090,      color: '#dde3ee' },
  { name: 'PIONEER',   min: 1180,      color: '#e2b13c', glow: true },
  { name: 'LUMINARY',  min: 1270,      color: '#2dd4bf', glow: true },
];

export const tierOf = (elo: number): RankTier =>
  RANK_TIERS.filter(t => elo >= t.min).pop()!;

export const nextTierOf = (elo: number): RankTier | null =>
  RANK_TIERS.find(t => t.min > elo) ?? null;

export const REGION_ORDER = Object.keys(REGIONS);

/**
 * Always five regions, always canonical order. Badges are collection slots:
 * your Sciences badge lives in the same place forever, so you watch IT change
 * instead of watching the row reshuffle. Also why a zero-answer account still
 * sees five locked badges: the engine emits nothing for unplayed regions, and
 * the empty state must show the collection, not a void.
 */
export function fullRegions(atlas: Atlas): RegionEstimate[] {
  return REGION_ORDER.map(k =>
    atlas.regions.find(r => r.region === k)
    ?? { region: k, name: REGIONS[k].name, elo: 1000, n: 0, tier: 'HIDDEN' as const }
  );
}

const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  return s.length % 2 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
};

/**
 * Rating over time, folded honestly through the same eloUpdate the engine
 * uses: per-region ratings accumulate answer by answer, the global line is the
 * median of regions with 10+ observations, sampled once per match (10 answers).
 */
export function ratingTimeline(events: AnswerEvent[]): number[] {
  const elo: Record<string, number> = {};
  const n: Record<string, number> = {};
  const out: number[] = [];
  events.forEach((e, i) => {
    const region = regionOfRealm(e.realm);
    if (region) {
      elo[region] = eloUpdate(elo[region] ?? 1000, e.difficulty, e.correct && !e.timedOut, n[region] ?? 0);
      n[region] = (n[region] ?? 0) + 1;
    }
    if ((i + 1) % 10 === 0 || i === events.length - 1) {
      const visible = Object.keys(elo).filter(k => (n[k] ?? 0) >= 10).map(k => elo[k]);
      if (visible.length > 0) out.push(Math.round(median(visible)));
    }
  });
  return out;
}

export interface RegionDelta {
  region: string;
  name: string;
  before: number;
  after: number;
}

export interface MatchDeltaResult {
  /** Regions this match moved, biggest move first. */
  changes: RegionDelta[];
  /** Global rating after the match, null while unranked. */
  rating: number | null;
  next: RankTier | null;
  /** While unranked: the region closest to its first rank, and how far. */
  placement: { name: string; n: number; need: number } | null;
}

/** Answers a region needs before it carries a rating (cognitiveEngine). */
export const PLACEMENT_ANSWERS = 10;

/**
 * What the last match did to the numbers: the full history minus the match is
 * replayed against the full history, so every figure is exactly what the
 * engine says, never an estimate layered on top.
 */
export function computeMatchDelta(events: AnswerEvent[], lastN: number): MatchDeltaResult | null {
  if (events.length === 0 || lastN <= 0 || lastN > events.length) return null;
  const before = computeAtlas(events.slice(0, events.length - lastN));
  const after = computeAtlas(events);
  const changes: RegionDelta[] = after.regions
    .map(r => {
      const prev = before.regions.find(p => p.region === r.region);
      return {
        region: r.region,
        name: r.name,
        before: Math.round(prev?.elo ?? 1000),
        after: Math.round(r.elo),
      };
    })
    .filter(c => c.after !== c.before)
    .sort((a, b) => Math.abs(b.after - b.before) - Math.abs(a.after - a.before));
  if (changes.length === 0) return null;
  let placement: MatchDeltaResult['placement'] = null;
  if (after.globalElo === null && after.regions.length > 0) {
    const closest = [...after.regions].sort((a, b) => b.n - a.n)[0];
    placement = { name: closest.name, n: Math.min(closest.n, PLACEMENT_ANSWERS), need: PLACEMENT_ANSWERS };
  }
  return {
    changes,
    rating: after.globalElo,
    next: after.globalElo !== null ? nextTierOf(after.globalElo) : null,
    placement,
  };
}
